import ReturnTrialError from '@features/subscription/ReturnTrialError';
import { RETURN_TRIAL_PATH, returnTrialLoader } from '@features/subscription/returnTrial';
import CheckoutPage from './features/checkout/CheckoutPage';
import { Navigate, RouteObject, Outlet } from 'react-router-dom';
import BookingPage from '@features/customer-reservations/pages/booking-page';
import ImportContactsPage from '@features/client/components/ImportContactsPage';
import Layout from '@shared/components/Layout';
import MyProfile from '@pages/profile';
import ClientsList from '@pages/clients-page';
import ShoppingList from '@pages/shopping-list';
import PriceList from '@pages/price-list';
import PricingCalculator from '@pages/price-calculator';
import VisitDetail from '@features/visits/components/visit-detail/visit-detail';
import ClientProfile from '@pages/client-profile';
import { SubscriptionPage } from '@pages/subscription-page';
import { ActivationPage } from '@features/customer-reservations/pages/ActivationPage';
import { BrowserGuidePage } from '@features/customer-reservations/pages/BrowserGuidePage';
import { PhoneLoginPage } from '@features/customer-reservations/pages/PhoneLoginPage';
import { SetPinPage } from '@features/customer-reservations/pages/SetPinPage';
import { ResetPinPage } from '@features/customer-reservations/pages/ResetPinPage';
import { Dashboard } from '@pages/dashboard-page/dashboard-page';
import RouterErrorBoundary from '@pages/error-page';
import PageBoundary from '@shared/components/PageBoundary';
import AllVisitsContainer from '@pages/visits-page';
import UsersOverview from '@pages/users-overview';
import AuthGate from '@shared/auth/AuthGate';
import Root from './root';
import requiredSubscriptionLoader from '@features/subscription/requiredSubscriptionLoader';
import AuthSync from '@shared/auth/AuthSync';
import SubscriptionGuard from '@shared/components/SubscriptionGuard';
import { ROUTES } from '@shared/AppRoutes';
import { CustomerAuthProvider } from '@features/customer-reservations/CustomerAuthContext';
import { CUSTOMER_ROUTES } from '@features/customer-reservations/routes';
import Consumption from '@pages/consumption-page';
import Logs from '@pages/logs-page';
import ReceivedPaymentsPage from '@pages/received-payments-page';
import Stock from '@pages/stock-page';
import Team from '@pages/team-page';
import PublicProfilePage from '@features/public-reservation/PublicProfilePage';
import PublicBookingPage from '@features/public-reservation/PublicBookingPage';
import PublicSetPinPage from '@features/public-reservation/PublicSetPinPage';
import PendingReservationsPage from '@features/pending-reservations/PendingReservationsPage';

const routes: RouteObject[] = [
  {
    path: '/',
    errorElement: <RouterErrorBoundary />,
    element: (
      <PageBoundary>
        <Root />
      </PageBoundary>
    ),
    children: [
      { path: '/checkout', element: <CheckoutPage /> },
      {
        path: '/public-reservation/profile/:userId',
        element: (
          <CustomerAuthProvider>
            <Outlet />
          </CustomerAuthProvider>
        ),
        children: [
          {
            index: true,
            element: <PublicProfilePage />,
          },
          {
            path: 'booking',
            element: <PublicBookingPage />,
          },
          {
            path: 'set-pin',
            element: <PublicSetPinPage />,
          },
        ],
      },
      {
        // Legacy URL – backward compatibility
        path: '/public-reservations/:userId/booking',
        element: (
          <CustomerAuthProvider>
            <PublicBookingPage />
          </CustomerAuthProvider>
        ),
      },
      {
        path: '/customer-reservations',
        element: (
          <CustomerAuthProvider>
            <Outlet />
          </CustomerAuthProvider>
        ),
        children: [
          {
            index: true,
            element: <ActivationPage />,
          },
          {
            path: CUSTOMER_ROUTES.browserGuide.path,
            element: <BrowserGuidePage />,
          },
          {
            path: CUSTOMER_ROUTES.login.path,
            element: <PhoneLoginPage />,
          },
          {
            path: CUSTOMER_ROUTES.setPin.path,
            element: <SetPinPage />,
          },
          {
            path: CUSTOMER_ROUTES.resetPin.path,
            element: <ResetPinPage />,
          },
          {
            path: CUSTOMER_ROUTES.booking.path,
            element: <BookingPage />,
          },
        ],
      },
      {
        element: <AuthSync />,
        children: [
          {
            index: true,
            element: <Navigate to="/hairdresser" replace />,
          },
          {
            path: '/hairdresser',
            children: [
              {
                element: <AuthGate />,
                children: [
                  {
                    path: RETURN_TRIAL_PATH,
                    loader: returnTrialLoader,
                    errorElement: <ReturnTrialError />,
                    element: <SubscriptionPage />,
                  },
                  {
                    path: ROUTES.subscription.path,
                    element: <SubscriptionPage />,
                  },
                  {
                    path: ROUTES.importContactsPage.path,
                    element: <ImportContactsPage />,
                  },
                  {
                    element: <SubscriptionGuard />,
                    children: [
                      {
                        element: <Layout />,
                        children: [
                          {
                            index: true,
                            path: ROUTES.home.path,
                            element: <Dashboard />,
                          },
                          {
                            path: ROUTES.profile.path,
                            element: <MyProfile />,
                          },
                          {
                            path: ROUTES.clients.path,
                            element: <ClientsList />,
                          },
                          {
                            path: ROUTES.shoppingList.path,
                            element: <ShoppingList />,
                          },
                          {
                            path: ROUTES.consumption.path,
                            element: <Consumption />,
                          },
                          {
                            path: ROUTES.services.path,
                            element: <PriceList />,
                          },
                          {
                            path: ROUTES.logs.path,
                            element: <Logs />,
                          },
                          {
                            path: ROUTES.receivedPayments.path,
                            loader: () =>
                              requiredSubscriptionLoader(
                                ROUTES.receivedPayments.allowedSubscriptionPlans,
                              ),
                            element: <ReceivedPaymentsPage />,
                          },
                          {
                            path: ROUTES.usersOverview.path,
                            element: <UsersOverview />,
                          },
                          {
                            path: ROUTES.pricingCalculator.path,
                            loader: () =>
                              requiredSubscriptionLoader(
                                ROUTES.pricingCalculator.allowedSubscriptionPlans,
                              ),
                            element: <PricingCalculator />,
                          },
                          {
                            path: ROUTES.visits.path,
                            element: <AllVisitsContainer type="allVisitsPage" />,
                          },
                          {
                            path: ROUTES.visitDetail.path,
                            element: <VisitDetail />,
                          },
                          {
                            path: ROUTES.team.path,
                            element: <Team />,
                          },
                          {
                            path: ROUTES.clientDetail.path,
                            element: <ClientProfile />,
                          },
                          {
                            path: ROUTES.stock.path,
                            element: <Stock />,
                          },

                          {
                            path: ROUTES.pendingReservations.path,
                            element: <PendingReservationsPage />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: '*',
            element: <Navigate to="/hairdresser" replace />,
          },
        ],
      },
    ],
  },
];

export default routes;
